import { useState } from 'react';

interface SparklineProps {
  values: number[];
  color?: string;
  height?: number;
  width?: number;
  strokeWidth?: number;
  /** Formatted [min, max] labels for the left y-axis gutter. Omit for a bare line. */
  axisLabels?: { min: string; max: string };
  /** Pre-formatted readout per point (aligned to `values`). Enables hover/tap tooltips. */
  tooltips?: string[];
}

const GRID = '#e5e5e5';   // slate-200

export function Sparkline({
  values,
  color = '#3b82f6',
  height = 40,
  width = 200,
  strokeWidth = 2,
  axisLabels,
  tooltips,
}: SparklineProps) {
  const [active, setActive] = useState<number | null>(null);

  // Need at least 2 points to draw a line.
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const mLeft = axisLabels ? 30 : 0;
  // Clearance for the endpoint/active-point dot's radius (r=3 max) — without
  // it the last point's cx lands exactly on the viewBox's right edge and the
  // dot's right half gets clipped, rendering as a semicircle.
  const mRight = axisLabels || tooltips ? 4 : 0;
  const mY = axisLabels ? 5 : 0; // top/bottom padding so labels/dots aren't clipped
  const plotW = width - mLeft - mRight;
  const plotH = height - mY * 2;

  // Y-axis labels are rendered as fixed-size HTML (not SVG <text>): SVG text
  // scales with the viewBox, so in the narrow 2-up sidebar cards it shrank to
  // ~6px and became unreadable. HTML labels stay legible at any card width.
  // Positioned on the gridlines as a % of height so they track mY/plotH.
  const maxTopPct = (mY / height) * 100;
  const minTopPct = ((mY + plotH) / height) * 100;

  const step = plotW / (values.length - 1);
  const coords = values.map((v, i) => ({
    x: mLeft + i * step,
    y: mY + plotH - ((v - min) / range) * plotH,
  }));
  const points = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];

  const activePt = active != null ? coords[active] : null;
  const tipLeftPct = activePt ? Math.min(94, Math.max(6, (activePt.x / width) * 100)) : 0;

  return (
    <div className="relative">
      {active != null && activePt && tooltips?.[active] && (
        <div
          className="pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-stone-900 px-2 py-1 text-[10px] font-medium leading-tight text-white"
          style={{ left: `${tipLeftPct}%` }}
        >
          {tooltips[active]}
        </div>
      )}
      {axisLabels && (
        <>
          <span
            className="pointer-events-none absolute left-0 z-1 text-[10px] font-medium leading-none text-muted-foreground tabular-nums"
            style={{ top: `${maxTopPct}%`, transform: 'translateY(-50%)' }}
          >
            {axisLabels.max}
          </span>
          <span
            className="pointer-events-none absolute left-0 z-1 text-[10px] font-medium leading-none text-muted-foreground tabular-nums"
            style={{ top: `${minTopPct}%`, transform: 'translateY(-50%)' }}
          >
            {axisLabels.min}
          </span>
        </>
      )}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
        {axisLabels && (
          <>
            <line x1={mLeft} y1={mY} x2={width} y2={mY} stroke={GRID} strokeWidth={1} />
            <line x1={mLeft} y1={mY + plotH} x2={width} y2={mY + plotH} stroke={GRID} strokeWidth={1} />
          </>
        )}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Endpoint dot (latest value), or the active point when hovering/tapping. */}
        {activePt ? (
          <circle cx={activePt.x} cy={activePt.y} r={3} fill={color} stroke="#fff" strokeWidth={1.5} />
        ) : (
          axisLabels && last && <circle cx={last.x} cy={last.y} r={2.5} fill={color} />
        )}

        {/* Transparent per-point hit slices for hover/tap readout. */}
        {tooltips &&
          coords.map((c, i) => (
            <rect
              key={`hit-${i}`}
              x={Math.max(mLeft, c.x - step / 2)}
              y={0}
              width={step}
              height={height}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onPointerEnter={() => setActive(i)}
              onPointerDown={() => setActive(i)}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setActive((cur) => (cur === i ? null : cur));
              }}
              aria-hidden
            />
          ))}
      </svg>
    </div>
  );
}
